import React from "react";
import { useBlocker } from "react-router";
import { confirmInGrain } from "../utils/grainDialogs.js";

// La fiche d'une entreprise de facturation donne son message (changements de la fiche et de ses associations).
export default function useBillingAliasesLeaveGuard({ dirty, saving, discard, onGuardChange, message }) {
  const current = React.useRef({});
  const mounted = React.useRef(true);
  const navigationPending = React.useRef(false);
  current.current = { dirty, saving, discard, message };
  React.useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const allowLeave = React.useCallback(async () => {
    if (current.current.saving) return false;
    if (!current.current.dirty) return true;
    if (!await confirmInGrain(current.current.message, { title: "Abandonner les modifications ?", confirmLabel: "Abandonner" })) return false;
    if (!mounted.current || current.current.saving) return false;
    current.current.dirty = false;
    current.current.discard();
    return true;
  }, []);
  const blocker = useBlocker(() => current.current.saving || current.current.dirty);
  const latestBlocker = React.useRef(blocker);
  latestBlocker.current = blocker;
  React.useEffect(() => {
    if (blocker.state !== "blocked" || navigationPending.current) return;
    navigationPending.current = true;
    void allowLeave().then(allowed => {
      if (!mounted.current || latestBlocker.current.state !== "blocked") return;
      if (allowed) latestBlocker.current.proceed();
      else latestBlocker.current.reset();
    }).finally(() => { navigationPending.current = false; });
  }, [allowLeave, blocker]);
  React.useEffect(() => {
    onGuardChange?.(allowLeave);
    const beforeUnload = (event) => {
      if (!current.current.dirty && !current.current.saving) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      onGuardChange?.(null);
      window.removeEventListener("beforeunload", beforeUnload);
    };
  }, [allowLeave, onGuardChange]);
  return allowLeave;
}
